import React, { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { createProperty } from "../../../../../../api/services/hostService";
import { apiClient } from "../../../../../../api/HttpClient";
import { useUserContext } from "../../../../../protectedRoute/UserContext";
import PostLoginHeader from "../../../../../../components/PostLoginHeader/PostLogin";
import LocationPicker from "../../../../../../components/common/LocationPicker";
import axios from 'axios';
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../../../components/ui/select";
import { Textarea } from "../../../../../../components/ui/textarea";
import { Button } from "../../../../../../components/ui/button";
import { Upload, MapPin, Utensils, AlertTriangle, Loader2, Plus } from "lucide-react";
import { Snackbar, Alert } from "@mui/material";
import { MultiSelect } from "../../../../../../components/ui/multi-select";

interface ImageQualityError {
    name: string;
    reason: string;
}

const MIN_WIDTH = 400;
const MIN_HEIGHT = 300;
const MIN_FILE_SIZE_KB = 30;
const BLUR_THRESHOLD = 80;

function checkImageQuality(file: File): Promise<{ valid: boolean; reason?: string }> {
    if (file.size < MIN_FILE_SIZE_KB * 1024) {
        return Promise.resolve({
            valid: false,
            reason: `File size too small (${Math.round(file.size / 1024)}KB). Minimum ${MIN_FILE_SIZE_KB}KB required.`,
        });
    }

    return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
                resolve({
                    valid: false,
                    reason: `Resolution too low (${img.width}×${img.height}px). Minimum ${MIN_WIDTH}×${MIN_HEIGHT}px required.`,
                });
                return;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (!ctx) { resolve({ valid: true }); return; }

            const sw = 100;
            const sh = 100;
            canvas.width = sw;
            canvas.height = sh;
            ctx.drawImage(img, 0, 0, sw, sh);

            const { data: pixels } = ctx.getImageData(0, 0, sw, sh);
            const grayscale: number[] = [];
            let totalBrightness = 0;

            for (let i = 0; i < pixels.length; i += 4) {
                const gray = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
                grayscale.push(gray);
                totalBrightness += gray;
            }

            const avgBrightness = totalBrightness / (sw * sh);
            if (avgBrightness < 30) {
                resolve({ valid: false, reason: "Image is too dark. Please upload a well-lit photo." });
                return;
            }
            if (avgBrightness > 240) {
                resolve({ valid: false, reason: "Image is overexposed. Please upload a properly lit photo." });
                return;
            }

            // Laplacian variance for blur detection
            let laplacianSum = 0;
            for (let y = 1; y < sh - 1; y++) {
                for (let x = 1; x < sw - 1; x++) {
                    const idx = y * sw + x;
                    const lap = Math.abs(
                        4 * grayscale[idx] -
                        grayscale[idx - sw] -
                        grayscale[idx + sw] -
                        grayscale[idx - 1] -
                        grayscale[idx + 1]
                    );
                    laplacianSum += lap * lap;
                }
            }
            const blurScore = laplacianSum / (sw * sh);
            if (blurScore < BLUR_THRESHOLD) {
                resolve({ valid: false, reason: "Image appears blurry. Please upload a sharp, clear photo." });
                return;
            }

            resolve({ valid: true });
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({ valid: false, reason: "Could not read image file." });
        };

        img.src = url;
    });
}

// Define the form data type
interface Location {
    latitude: number;
    longitude: number;
    street_address: string;
    city: string;
    locality: string;
}

interface FormData {
    host: number;
    image: any;
    name: string;
    location: Location;
    property_type: string;
    property_gender_type: string;
    description: string;
    facilities: string[];
    food_available: boolean;
    highlights: string[];
    property_rules: string[];
}

interface CreatePropertyPageProps {
    showInDialog?: boolean;
    onClose?: () => void;
    onPropertyCreated?: () => void;
}

export default function CreatePropertyPage({ showInDialog = false, onClose, onPropertyCreated }: CreatePropertyPageProps) {
    const { register, handleSubmit, watch, formState: { errors }, setError, reset, setValue, control } = useForm<FormData>({
        defaultValues: {
            facilities: [],
            highlights: [],
            property_rules: [],
            image: []
        },
    });
    const { state } = useUserContext();
    const { hostID } = state;
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imageQualityErrors, setImageQualityErrors] = useState<ImageQualityError[]>([]);
    const [isCheckingImages, setIsCheckingImages] = useState(false);
    const selectedFacilities = watch("facilities") || [];

    const navigate = useNavigate();
    
    const uploadFile = async (id: any) => {
        const formData = new FormData();
        imageFiles.forEach((file) => {
            formData.append("image", file);
        });
        formData.append("id", id);
        try {
            const client = await apiClient;
            const response = await client.post('/api/hosts/files/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            console.log("uploadFile response", response);
        } catch (error) {
            console.log("uploadFile error", error);
        }
    }

    const onSubmit = async (data: any) => {
        const hostIDParsed = hostID ? parseInt(hostID) : NaN;
        data.host = hostIDParsed;

        const formData = new FormData();
        
        formData.append("name", data.name);
        formData.append("description", data.description);
        formData.append("property_type", data.property_type);
        formData.append("property_gender_type", data.property_gender_type);
        formData.append("food_available", data.food_available);
        formData.append("host", data.host.toString());
        formData.append("location", JSON.stringify(data.location));
        formData.append("facilities", JSON.stringify(data.facilities));
        formData.append("highlights", JSON.stringify(data.highlights));
        formData.append("property_rules", JSON.stringify(data.property_rules));
        formData.append("data", JSON.stringify(data));
        
        imageFiles.forEach((file) => {
            formData.append("image", file);
        });

        setIsLoading(true);
    
        try {
            const response = await createProperty(formData);
            console.log("API Response:", response);
    
            if (response?.id) {
                uploadFile(response.id)
                setSnackbarMessage("Property created successfully!");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                reset();
                if (onPropertyCreated) {
                    onPropertyCreated();
                }
                if (onClose) {
                    onClose();
                } else {
                    navigate('/home', { state: { propertyCreationSuccess: true } });
                }
            }
        } catch (error) {
            console.error("Error creating property:", error);
            setSnackbarMessage("Failed to create property");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const files = Array.from(event.target.files);
        setIsCheckingImages(true);
        setImageQualityErrors([]);

        const accepted: File[] = [];
        const rejected: ImageQualityError[] = [];

        for (const file of files) {
            const result = await checkImageQuality(file);
            if (result.valid) {
                accepted.push(file);
            } else {
                rejected.push({ name: file.name, reason: result.reason! });
            }
        }

        setIsCheckingImages(false);
        if (accepted.length > 0) {
            setImageFiles((prev) => [...prev, ...accepted]);
        }
        if (rejected.length > 0) {
            setImageQualityErrors(rejected);
        }
    };

      const removeImage = (index: number) => {
        setImageFiles((prev) => prev.filter((_, i) => i !== index));
      };

    const [facilityOptions, setFacilityOptions] = useState([
        { value: "wifi", label: "WiFi" },
        { value: "ac", label: "AC" },
        { value: "parking", label: "Parking" },
        { value: "laundry", label: "Laundry" },
        { value: "gym", label: "Gym" },
        { value: "security", label: "24/7 Security" },
    ]);

    const [highlightsOptions, setHighlightsOptions] = useState([
        { value: "metro-nearby", label: "Metro Nearby" },
        { value: "market-nearby", label: "Market Nearby" },
        { value: "hospital-nearby", label: "Hospital Nearby" },
        { value: "college-nearby", label: "College Nearby" },
        { value: "it-park", label: "IT Park Nearby" },
    ]);

    const [propertyRulesOptions, setPropertyRulesOptions] = useState([
        { value: "no-smoking", label: "No Smoking" },
        { value: "no-drinking", label: "No Drinking" },
        { value: "no-pets", label: "No Pets" },
        { value: "guest-timing", label: "Guest Timing" },
        { value: "gate-closing", label: "Gate Closing Time" },
    ]);

    const [customFacility, setCustomFacility] = useState("");
    const [customHighlight, setCustomHighlight] = useState("");
    const [customRule, setCustomRule] = useState("");

    const addCustomOption = (
        rawLabel: string,
        options: { value: string; label: string }[],
        setOptions: React.Dispatch<React.SetStateAction<{ value: string; label: string }[]>>,
        field: "facilities" | "highlights" | "property_rules",
        clearInput: () => void,
    ) => {
        const label = rawLabel.trim();
        if (!label) return;
        const value = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
        if (!value) return;
        if (!options.some((o) => o.value === value)) {
            setOptions([...options, { value, label }]);
        }
        const currentSelected: string[] = (watch(field) as string[]) || [];
        if (!currentSelected.includes(value)) {
            setValue(field, [...currentSelected, value]);
        }
        clearInput();
    };

    return (
        <>
            {!showInDialog && <PostLoginHeader />}
            <div className="bg-background p-4 md:p-8">
                <div className="max-w-4xl mx-auto bg-card p-6 rounded-lg shadow-md">
                    <div className="space-y-2">
                        <h2 className="text-xl font-bold text-primary">Create Property</h2>
                        <p className="text-muted-foreground">
                            Fill in the details to list your property
                        </p>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="py-4">
                        <div className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="name">Property Name *</Label>
                                <Input 
                                    id="name"
                                    placeholder="Enter property name"
                                    {...register("name", { required: "Property Name is required" })}
                                    className="bg-background"
                                />
                                {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="street_address" className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    Street Address *
                                </Label>
                                <Input 
                                    id="street_address"
                                    placeholder="Enter street address"
                                    {...register("location.street_address", { required: "Street Address is required" })}
                                    className="bg-background"
                                />
                                {errors.location?.street_address && <p className="text-red-500 text-sm">{errors.location.street_address.message}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="city">City *</Label>
                                    <Controller
                                        name="location.city"
                                        control={control}
                                        rules={{ required: "Please select a city" }}
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className="bg-background">
                                                    <SelectValue placeholder="Select City" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-card border z-50">
                                                    <SelectItem value="bangalore">Bangalore</SelectItem>
                                                    <SelectItem value="mumbai">Mumbai</SelectItem>
                                                    <SelectItem value="delhi">Delhi</SelectItem>
                                                    <SelectItem value="hyderabad">Hyderabad</SelectItem>
                                                    <SelectItem value="chennai">Chennai</SelectItem>
                                                    <SelectItem value="pune">Pune</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {errors.location?.city && <p className="text-red-500 text-sm">{errors.location.city.message}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="locality">Locality *</Label>
                                    <Input 
                                        id="locality"
                                        placeholder="Enter locality"
                                        {...register("location.locality", { required: "Locality is required" })}
                                        className="bg-background"
                                    />
                                    {errors.location?.locality && <p className="text-red-500 text-sm">{errors.location.locality.message}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Property Type *</Label>
                                    <Controller
                                        name="property_type"
                                        control={control}
                                        rules={{ required: "Please select a property type" }}
                                        render={({ field }) => (
                                            <Select onValueChange={(v) => {
                                                field.onChange(v);
                                                if (v === 'hostel') setValue('property_gender_type', 'unisex');
                                            }} value={field.value}>
                                                <SelectTrigger className="bg-background">
                                                    <SelectValue placeholder="Select Type" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-card border z-50">
                                                    <SelectItem value="pg">PG</SelectItem>
                                                    <SelectItem value="hostel">Hostel</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {errors.property_type && <p className="text-red-500 text-sm">{errors.property_type.message}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label>Property Gender Type *</Label>
                                    <Controller
                                        name="property_gender_type"
                                        control={control}
                                        rules={{ required: "Please select a gender type" }}
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className="bg-background">
                                                    <SelectValue placeholder="Select Gender" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-card border z-50">
                                                    {watch("property_type") !== 'hostel' && <SelectItem value="male">Male Only</SelectItem>}
                                                    {watch("property_type") !== 'hostel' && <SelectItem value="female">Female Only</SelectItem>}
                                                    <SelectItem value="unisex">Co-ed</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {errors.property_gender_type && <p className="text-red-500 text-sm">{errors.property_gender_type.message}</p>}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description *</Label>
                                <Textarea
                                    id="description"
                                    placeholder="Describe your property..."
                                    {...register("description", { required: "Description is required" })}
                                    className="bg-background min-h-[80px]"
                                />
                                {errors.description && <p className="text-red-500 text-sm">{errors.description.message}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Facilities *</Label>
                                    <MultiSelect
                                        options={facilityOptions}
                                        value={watch("facilities")}
                                        onChange={(value) => setValue("facilities", value)}
                                        placeholder="Select Facilities"
                                    />
                                    <div className="flex gap-2">
                                        <Input
                                            value={customFacility}
                                            onChange={(e) => setCustomFacility(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addCustomOption(customFacility, facilityOptions, setFacilityOptions, "facilities", () => setCustomFacility(""));
                                                }
                                            }}
                                            placeholder="Add a custom facility"
                                            className="bg-background"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addCustomOption(customFacility, facilityOptions, setFacilityOptions, "facilities", () => setCustomFacility(""))}
                                        >
                                            <Plus className="h-4 w-4 mr-1" /> Add
                                        </Button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Utensils className="h-4 w-4 text-muted-foreground" />
                                        Food Available *
                                    </Label>
                                    <Controller
                                        name="food_available"
                                        control={control}
                                        rules={{ validate: (v) => v !== undefined && v !== null ? true : "Please select food availability" }}
                                        render={({ field }) => (
                                            <Select onValueChange={(value) => field.onChange(value === "true")} value={field.value?.toString()}>
                                                <SelectTrigger className="bg-background">
                                                    <SelectValue placeholder="Food Available" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-card border z-50">
                                                    <SelectItem value="true">Yes</SelectItem>
                                                    <SelectItem value="false">No</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {errors.food_available && <p className="text-red-500 text-sm">{errors.food_available.message}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Highlights *</Label>
                                    <MultiSelect
                                        options={highlightsOptions}
                                        value={watch("highlights")}
                                        onChange={(value) => setValue("highlights", value)}
                                        placeholder="Select Highlights"
                                    />
                                    <div className="flex gap-2">
                                        <Input
                                            value={customHighlight}
                                            onChange={(e) => setCustomHighlight(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addCustomOption(customHighlight, highlightsOptions, setHighlightsOptions, "highlights", () => setCustomHighlight(""));
                                                }
                                            }}
                                            placeholder="Add a custom highlight"
                                            className="bg-background"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addCustomOption(customHighlight, highlightsOptions, setHighlightsOptions, "highlights", () => setCustomHighlight(""))}
                                        >
                                            <Plus className="h-4 w-4 mr-1" /> Add
                                        </Button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Property Rules *</Label>
                                    <MultiSelect
                                        options={propertyRulesOptions}
                                        value={watch("property_rules")}
                                        onChange={(value) => setValue("property_rules", value)}
                                        placeholder="Select Rules"
                                    />
                                    <div className="flex gap-2">
                                        <Input
                                            value={customRule}
                                            onChange={(e) => setCustomRule(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addCustomOption(customRule, propertyRulesOptions, setPropertyRulesOptions, "property_rules", () => setCustomRule(""));
                                                }
                                            }}
                                            placeholder="Add a custom rule"
                                            className="bg-background"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addCustomOption(customRule, propertyRulesOptions, setPropertyRulesOptions, "property_rules", () => setCustomRule(""))}
                                        >
                                            <Plus className="h-4 w-4 mr-1" /> Add
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    Property Location *
                                </Label>
                                <div className="border border-border rounded-lg bg-muted/30 p-4">
                                    <input type="hidden" {...register("location.latitude", { required: "Please pick a location (use 'Use My Location' or enter coordinates manually).", valueAsNumber: true })} />
                                    <input type="hidden" {...register("location.longitude", { required: "Please pick a location (use 'Use My Location' or enter coordinates manually).", valueAsNumber: true })} />
                                    <LocationPicker
                                        initialLat={watch("location.latitude")}
                                        initialLng={watch("location.longitude")}
                                        onLocationChange={(lat, lng) => {
                                            setValue("location.latitude", lat, { shouldValidate: true });
                                            setValue("location.longitude", lng, { shouldValidate: true });
                                        }}
                                    />
                                </div>
                                {errors.location?.latitude && <p className="text-red-500 text-sm">{errors.location.latitude.message?.toString() || "Please select a location on the map."}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label>Property Images *</Label>
                                <Label htmlFor="image-upload" className="w-full border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center gap-2 hover:border-primary/50 transition-colors cursor-pointer">
                                    {isCheckingImages ? (
                                        <>
                                            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                                            <span className="text-sm text-muted-foreground">Checking image quality…</span>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-8 w-8 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">Click to upload images</span>
                                            <span className="text-xs text-muted-foreground">Min {MIN_WIDTH}×{MIN_HEIGHT}px · Min {MIN_FILE_SIZE_KB}KB · Sharp &amp; well-lit</span>
                                        </>
                                    )}
                                </Label>
                                <Input
                                    type="file"
                                    multiple
                                    className="hidden"
                                    id="image-upload"
                                    accept="image/*"
                                    disabled={isCheckingImages}
                                    {...register("image", {
                                        validate: () => imageFiles.length > 0 || "At least one image is required",
                                        onChange: handleImageUpload,
                                    })}
                                />
                                {errors.image && typeof errors.image.message === 'string' && (
                                    <p className="text-red-500 text-sm">{errors.image.message}</p>
                                )}

                                {imageQualityErrors.length > 0 && (
                                    <div className="rounded-lg border border-yellow-400 bg-yellow-50 p-3 space-y-1">
                                        <div className="flex items-center gap-2 text-yellow-700 font-medium text-sm">
                                            <AlertTriangle className="h-4 w-4 shrink-0" />
                                            {imageQualityErrors.length} image{imageQualityErrors.length > 1 ? "s" : ""} rejected due to quality issues:
                                        </div>
                                        <ul className="list-disc list-inside space-y-0.5">
                                            {imageQualityErrors.map((err, i) => (
                                                <li key={i} className="text-xs text-yellow-700">
                                                    <span className="font-medium">{err.name}</span> — {err.reason}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-4 mt-4">
                                    {imageFiles.map((file, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={`Preview ${index}`}
                                                className="w-32 h-32 object-cover rounded-lg"
                                            />
                                            <Button
                                                variant="destructive"
                                                onClick={() => removeImage(index)}
                                                className="absolute top-1 right-1"
                                                size="sm"
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-2 border-t">
                            <Button variant="outline" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? "Creating..." : "Create Property"}
                            </Button>
                        </div>
                    </form>
                </div>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </div>
        </>
    );
}